import type { Variant } from '@components/button';
import {
    BORDER_COLORS as BC,
    DEFAULT_COLORS as DC,
    SURFACE_COLORS as SC,
    TEXT_COLORS as TC,
} from '@constants/colors.ts';
import { DESKTOP_SIZES, FONT_FAMILY, SIZES } from '@constants/fonts.ts';
import { Button as AntButton } from 'antd';
import styled, { css } from 'styled-components';

const DEFAULT_STYLING = css`
    padding: 8px 16px;
    border-radius: 8px;
    height: 40px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    /* body/sm/sm-medium */
    ${FONT_FAMILY.poppins};
    ${DESKTOP_SIZES[SIZES.BODY_MD].fontSize}
    ${DESKTOP_SIZES[SIZES.BODY_MD].lineHeight}
    ${DESKTOP_SIZES[SIZES.BODY_MD].fontWeight}
`;

const VARIANT_STYLES: Record<Variant, ReturnType<typeof css>> = {
    primary: css`
        border: 1px solid ${BC.focus};
        background: ${BC.focus};
        color: ${DC.white};

        &&:hover {
            background: ${SC.actionHover};
            border: 1px solid ${BC.hover};
            color: ${DC.white};
            span[role='img'] {
                color: ${DC.white};
            }
        }

        &&:disabled {
            background: ${BC.primary};
            border: 1px solid ${BC.disabled};
            cursor: not-allowed;
            color: ${TC.disabled};
            span[role='img'] {
                color: ${TC.disabled};
            }
        }

        &&:focus {
            background: ${BC.focus};
            border: 1px solid ${BC.focus};
            box-shadow:
                0 0 0 2px ${DC.white},
                0 0 0 4px ${BC.focus};
            color: ${DC.white};
            outline: none;
            span[role='img'] {
                color: ${DC.white};
            }
        }
    `,
    secondary: css`
        border: 1px solid rgba(33, 113, 198, 0.4);
        background: ${SC.page};
        color: ${BC.focus};

        &&:hover {
            background: #afd6ff;
            border: 1px solid ${BC.focus};
            color: ${SC.actionHover};
            span[role='img'] {
                color: ${SC.actionHover};
            }
        }

        &&:disabled {
            background: ${BC.primary};
            border: 1px solid ${BC.disabled};
            cursor: not-allowed;
            color: ${TC.disabled};
            span[role='img'] {
                color: ${TC.disabled};
            }
        }

        &&:focus {
            background: ${SC.page};
            border: 1px solid ${SC.page};
            box-shadow:
                0 0 0 2px ${DC.white},
                0 0 0 4px ${BC.focus};
            color: ${BC.focus};
            outline: none;
            span[role='img'] {
                color: ${BC.focus};
            }
        }
    `,
    outline: css`
        border: 1px solid rgba(33, 113, 198, 0.4);
        background: ${DC.white};
        color: ${BC.focus};
        &&:hover {
            background: ${SC.page};
            border: 1px solid ${BC.focus};
            color: ${BC.focus};
            span[role='img'] {
                color: ${BC.focus};
            }
        }

        &&:disabled {
            background: ${BC.primary};
            border: 1px solid ${BC.disabled};
            cursor: not-allowed;
            color: ${TC.disabled};
            span[role='img'] {
                color: ${TC.disabled};
            }
        }

        &&:focus {
            background: ${DC.white};
            border: 1px solid rgba(33, 113, 198, 0.4);
            box-shadow:
                0 0 0 2px ${DC.white},
                0 0 0 4px ${BC.focus};
            color: ${BC.focus};
            outline: none;
            span[role='img'] {
                color: ${BC.focus};
            }
        }
    `,
    transparent: css`
        border: none;
        background: ${DC.white};
        color: ${BC.focus};
        box-shadow: none;
        &&:hover {
            background: ${SC.page};
            color: ${BC.focus};
            span[role='img'] {
                color: ${BC.focus};
            }
        }

        &&:disabled {
            background: ${BC.primary};
            cursor: not-allowed;
            color: ${TC.disabled};
            span[role='img'] {
                color: ${TC.disabled};
            }
        }

        &&:focus {
            background: ${DC.white};
            box-shadow:
                0 0 0 2px ${DC.white},
                0 0 0 4px ${BC.focus};
            color: ${BC.focus};
            outline: none;
            span[role='img'] {
                color: ${BC.focus};
            }
        }
    `,
    success: css`
        color: ${DC.white};
        border: var(--border-width-sm_1, 1px) solid var(--colors-border-success, #22a06b);
        background: var(--colors-icons-success, #22a06b);
        &&:hover {
            border: var(--border-width-sm_1, 1px) solid var(--colors-green-600, #1d6e4f);
            background: var(--success-600, #1d6e4f);
            color: ${DC.white};
            span[role='img'] {
                color: ${DC.white};
            }
        }
        &&:disabled {
            background: ${BC.primary};
            border: 1px solid ${BC.disabled};
            cursor: not-allowed;
            color: ${TC.disabled};
            span[role='img'] {
                color: ${TC.disabled};
            }
        }
        &&:focus {
            border: 1px solid ${BC.success};
            box-shadow:
                0 0 0 2px ${DC.white},
                0 0 0 4px ${BC.success};
            color: ${DC.white};
            outline: none;
            span[role='img'] {
                color: ${DC.white};
            }
        }
    `,
    link: css`
        &.ant-btn {
            color: var(--colors-text-action, #2171c6);
            background: none;
            border: none;
            box-shadow: none;
        }
        &&:hover,
        &&:focus,
        &&:active {
            background: none !important;
            border: none !important;
            box-shadow: none !important;
        }
    `,
    purple: css`
        &.ant-btn {
            color: var(--colors-text-on-action, #fff);
            border: var(--border-width-sm_1, 1px) solid var(--new-default, #661887);
            background: var(--new-default, #661887);
            box-shadow: none;
        }
        &&:hover,
        &&:focus,
        &&:active {
            background: none;
            border: var(--border-width-sm_1, 1px) solid var(--new-default, #661887);
            background: var(--new-default, #661887);
            color: var(--colors-text-on-action, #fff);
        }
        &&:disabled {
            background: ${BC.primary};
            border: 1px solid ${BC.disabled};
            cursor: not-allowed;
            color: ${TC.disabled};
            span[role='img'] {
                color: ${TC.disabled};
            }
        }
    `,
    purpleOutline: css`
        &.ant-btn {
            color: var(--new-default, #661887);
            background: none;
            border: var(--border-width-sm_1, 1px) solid #cfa9df;
            box-shadow: none;
        }
        &&:hover,
        &&:focus,
        &&:active {
            background: none;
            border: var(--border-width-sm_1, 1px) solid #cfa9df;
            box-shadow: none;
            color: var(--new-default, #661887);
        }
        &&:disabled {
            background: ${BC.primary};
            border: 1px solid ${BC.disabled};
            cursor: not-allowed;
            color: ${TC.disabled};
            span[role='img'] {
                color: ${TC.disabled};
            }
        }
    `,
    warning: css`
        &.ant-btn {
            color: var(--colors-text-on-action, #fff);
            border: var(--border-width-sm_1, 1px) solid var(--colors-yellow-400, #eb9d06);
            background: var(--colors-yellow-400, #eb9d06);
            box-shadow: none;
        }
        &&:hover,
        &&:focus,
        &&:active {
            background: none;
            border: var(--border-width-sm_1, 1px) solid var(--colors-yellow-400, #eb9d06);
            background: var(--colors-yellow-400, #eb9d06);
            color: var(--colors-text-on-action, #fff);
        }
        &&:disabled {
            background: ${BC.primary};
            border: 1px solid ${BC.disabled};
            cursor: not-allowed;
            color: ${TC.disabled};
            span[role='img'] {
                color: ${TC.disabled};
            }
        }
    `,
};

const Button = styled(AntButton)<{ $customType?: Variant }>`
    &.ant-btn {
        ${DEFAULT_STYLING}
        ${({ $customType = 'primary' }) => VARIANT_STYLES[$customType]};
    }

    /* icon‐only buttons: square */
    &.ant-btn-icon-only {
        padding: 8px;
        width: 40px;
        justify-content: center;
    }

    /* make sure icons stay centered */
    span[role='img'] {
        line-height: 0;
    }

    &.full-width {
        width: 100%;
    }
`;

export default Button;
